import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative, not "@/…": lib/apiErrors.js is pure (no axios) and resolves for real under
// Jest, unlike "@/lib/api" (which this page's own test mocks only for `api.post`).
import { extractApiError } from "../../lib/apiErrors";

/**
 * `/rdv/confirmer?token=<manage_token>` — the reminder email's "Confirm" button
 * (contract, Automatic behaviours: `POST /api/bookings/manage/confirm {token}`,
 * idempotent, sets `confirmed_via_reminder_at`, emits `booking.confirmed_via_reminder`).
 *
 * NEVER MUTATES ON ARRIVAL (crm-decisions.md, "link scanners" — mail clients and
 * security tools open every mailed link to prescan it). The contract names no GET
 * companion for this route (unlike email unsubscribe, which has a landing read): there
 * is nothing to fetch, so the page renders a plain "Confirm" button and the write is the
 * client's own explicit click. The confirmed state offers no further control — a repeat
 * visit (a scanner, or the client re-opening the link) is a fresh click on a fresh mount,
 * and the endpoint being idempotent is what makes that safe.
 *
 * Registered host-agnostic on both public route trees (`routes/platformRoutes.jsx`,
 * `tenant/TenantSite.jsx`), same reasoning as `/gerer-rdv` and `/avis`: the link is
 * mailed under the practitioner's brand.
 */
export default function BookingConfirm() {
  const t = useT("public");
  const tc = useT("common");
  const { search } = useLocation();
  const token = useMemo(() => new URLSearchParams(search).get("token") || "", [search]);
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [error, setError] = useState("");

  const confirm = async () => {
    setState("busy");
    setError("");
    try {
      await api.post("/bookings/manage/confirm", { token });
      setState("done");
    } catch (e) {
      setError(extractApiError(e, tc("errors.saveFailed"), tc));
      setState("error");
    }
  };

  if (!token) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="crm-booking-confirm-invalid">
          <XCircle size={40} className="mx-auto text-eh-muted" />
          <h1 className="font-display text-3xl text-eh-ink mt-4">{t("confirm.missingTokenTitle")}</h1>
          <p className="text-eh-muted mt-3">{t("confirm.missingTokenBody")}</p>
        </section>
      </ThemedPageLayout>
    );
  }

  if (state === "done") {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="crm-booking-confirm-done">
          <CheckCircle2 size={40} className="mx-auto text-eh-sage-deep" />
          <h1 className="font-display text-3xl text-eh-ink mt-4">{t("confirm.doneTitle")}</h1>
          <p className="text-eh-muted mt-3">{t("confirm.doneBody")}</p>
        </section>
      </ThemedPageLayout>
    );
  }

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="crm-booking-confirm">
        <span className="eyebrow">{t("confirm.eyebrow")}</span>
        <h1 className="font-display text-3xl md:text-4xl text-eh-ink mt-3">{t("confirm.title")}</h1>
        <p className="text-eh-muted mt-3">{t("confirm.body")}</p>
        <button
          type="button"
          onClick={confirm}
          disabled={state === "busy"}
          data-testid="crm-booking-confirm-button"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-eh-ink text-eh-bg px-8 py-3 text-sm font-medium disabled:opacity-60"
        >
          {state === "busy" && <Loader2 className="animate-spin" size={16} aria-hidden="true" />}
          {t("confirm.button")}
        </button>
        {state === "error" && (
          <div role="alert" data-testid="crm-booking-confirm-error" className="mt-4 text-sm bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4">
            <p>{error}</p>
            <button type="button" onClick={confirm} className="mt-2 underline font-medium">{tc("actions.retry")}</button>
          </div>
        )}
      </section>
    </ThemedPageLayout>
  );
}
